import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import {
  IDIncomeVerificationStatus,
  VerificationFailureAction,
  VerificationType,
} from '@common/entities';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

export type VerificationFieldState = {
  isRequired: boolean;
  verificationFailureAction: VerificationFailureAction;
  status: IDIncomeVerificationStatus | null;
  metadata: PlaidLinkOnSuccessMetadata | null;
  fieldPageIndex: number | null;
  verificationType: VerificationType;
};

type VerificationEnabledState = {
  isIdVerificationEnabled: boolean;
  isIncomeVerificationEnabled: boolean;
  showPayrollVerification: boolean;
};

type SliceState = {
  idVerification: VerificationFieldState;
  incomeVerification: VerificationFieldState;
  isCompletionAllowed: boolean;
} & VerificationEnabledState;
export type InitializeIdIncomeSlicePayload = Partial<SliceState>;

const initialState: SliceState = {
  isIdVerificationEnabled: true,
  isIncomeVerificationEnabled: true,
  showPayrollVerification: false,
  idVerification: {
    isRequired: false,
    verificationFailureAction: VerificationFailureAction.DoNothing,
    status: IDIncomeVerificationStatus.NotStarted,
    metadata: null,
    fieldPageIndex: null,
    verificationType: VerificationType.IdentityVerification,
  },
  incomeVerification: {
    isRequired: false,
    verificationFailureAction: VerificationFailureAction.DoNothing,
    status: IDIncomeVerificationStatus.NotStarted,
    metadata: null,
    fieldPageIndex: null,
    verificationType: VerificationType.IncomeVerification,
  },
  isCompletionAllowed: true,
};

export const idIncomeVerificationSlice = createSlice({
  name: 'idIncomeVerification',
  initialState,
  reducers: {
    initializeIdIncomeSlice: (state, action: PayloadAction<InitializeIdIncomeSlicePayload>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setIDVerification: (state, action: PayloadAction<Partial<VerificationFieldState>>) => {
      return {
        ...state,
        idVerification: {
          ...state.idVerification,
          ...action.payload,
        },
      };
    },
    setIncomeVerification: (state, action: PayloadAction<Partial<VerificationFieldState>>) => {
      return {
        ...state,
        incomeVerification: {
          ...state.incomeVerification,
          ...action.payload,
        },
      };
    },
    setIsVerificationEnabled: (state, action: PayloadAction<Partial<VerificationEnabledState>>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setShowPayrollVerification: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showPayrollVerification: action.payload,
      };
    },
    setIsCompletionAllowed: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isCompletionAllowed: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
  },
});

export const {
  initializeIdIncomeSlice,
  setIDVerification,
  setIncomeVerification,
  setIsVerificationEnabled,
  setShowPayrollVerification,
  setIsCompletionAllowed,
} = idIncomeVerificationSlice.actions;

export default idIncomeVerificationSlice.reducer;
